import React, { FunctionComponent, useEffect, useMemo } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    Grid,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import styles from '../siu-surveillance-referral-form/SiuSurveillanceReferralForm.Styles';
import rtwSharedStyles from '../../referral-service/RtwShared.Styles';
import ServiceTypeRequestRtwEntity from '../../../../../entities/rtw/ServiceTypeRequestRtw.Entity';
import {
    getRtwAdditionalCommentsPlaceholder,
    RTW_ADDITIONAL_COMMENTS_MAX,
    RTW_ALTERNATE_HOURS_CHECKBOX_LABEL,
    RTW_ALTERNATE_SCHEDULE_CHECKBOX_LABEL,
    RTW_HOURS_PER_WEEK_FIELD_LABEL,
    RTW_HOURLY_RATE_MAX,
    RTW_LIGHT_DUTY_LOCATION_OPTIONS,
    RTW_PHYSICAL_RESTRICTIONS_MAX,
    RTW_PRIMARY_LANGUAGE_MAX,
    RTW_TRANSITION_HOURS_MAX,
    RTW_UNION_MEMBER_OPTIONS,
    RTW_WORK_SCHEDULE_MAX,
} from '../../referral-service/RtwServiceConstants';
import { isRtwFormValid, validateRtwFields } from '../../referral-service/RtwFieldValidation';

export interface IRtwReferralFormProps {
    rtwServiceTypeRequest: ServiceTypeRequestRtwEntity | null;
    onRtwServiceTypeRequestChange: (next: ServiceTypeRequestRtwEntity) => void;
    openCloseStatus?: Record<string, boolean>;
    handleOpenCloseStatus?: (boxName: string, isOpened: boolean) => void;
    handleSetValidationFunction?: (currentPageValidationFunction: () => boolean) => void;
    handleSetIsValidRtwFunction?: (currentPageIsValidRtwFunction: () => boolean, serviceTypeString: string) => void;
    showRtwValidationError?: boolean;
}

export const RtwReferralForm: FunctionComponent<IRtwReferralFormProps> = (props) => {
    const classes = styles();
    const rtwClasses = rtwSharedStyles();
    const model = props.rtwServiceTypeRequest ?? ({} as ServiceTypeRequestRtwEntity);

    const setModel = (patch: Partial<ServiceTypeRequestRtwEntity>) => {
        props.onRtwServiceTypeRequestChange({ ...model, ...patch });
    };

    const fieldValidationState = useMemo(
        () => ({
            physicalRestrictions: model.physicalRestrictions ?? '',
            primaryLanguage: model.primaryLanguage ?? '',
            hourlyRate: model.hourlyRate ?? '',
            isUnionMember: model.isUnionMember ?? '',
            lightDutyLocation: model.lightDutyLocation ?? '',
            requiresAlternateHoursEnabled: !!model.requiresAlternateHoursEnabled,
            transitionHoursPerWeek: model.transitionHoursPerWeek ?? '',
            requiresAlternateScheduleEnabled: !!model.requiresAlternateScheduleEnabled,
            workSchedulePreferences: model.workSchedulePreferences ?? '',
        }),
        [model],
    );

    const fieldErrors = useMemo(() => validateRtwFields(fieldValidationState), [fieldValidationState]);
    const showErrors = !!props.showRtwValidationError;

    useEffect(() => {
        if (props.handleOpenCloseStatus && props.openCloseStatus && !props.openCloseStatus.hasOwnProperty('RtwSection_box')) {
            props.handleOpenCloseStatus('RtwSection_box', true);
        }
    }, []);

    useEffect(() => {
        if (props.handleSetValidationFunction) {
            props.handleSetValidationFunction(() => isRtwFormValid(validateRtwFields(fieldValidationState)));
        }
        if (props.handleSetIsValidRtwFunction) {
            props.handleSetIsValidRtwFunction(
                () => isRtwFormValid(validateRtwFields(fieldValidationState)),
                'RTW',
            );
        }
    }, [fieldValidationState, props.handleSetValidationFunction, props.handleSetIsValidRtwFunction]);

    const handleOpenClose = (event: React.MouseEvent, boxName: string) => {
        event.persist();
        if (props.handleOpenCloseStatus && props.openCloseStatus) {
            props.handleOpenCloseStatus(boxName, !(props.openCloseStatus?.[boxName] ?? true));
        }
    };

    const fieldHelperProps = {
        FormHelperTextProps: { style: { marginTop: 4 } },
    };

    return (
        <Grid item xs={12}>
            <Accordion className={classes.paper} expanded={props.openCloseStatus?.['RtwSection_box'] ?? true}>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon className={classes.paperHeaderIcon} />}
                    className={classes.paperHeader}
                    onClick={(event) => handleOpenClose(event, 'RtwSection_box')}
                >
                    <Typography variant="subtitle2" className={classes.sidebarTitle}>
                        Return To Work
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Grid container spacing={2} className={classes.gridContainer}>
                        <Grid item xs={12}>
                            <FormControlLabel
                                className={rtwClasses.rtwCheckboxLabel}
                                control={
                                    <Checkbox
                                        checked={!!model.rushRequest}
                                        onChange={(_, checked) => setModel({ rushRequest: checked })}
                                        color="secondary"
                                    />
                                }
                                label="This is a Rush Request"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Current Physical Restrictions provided by a treating physician"
                                required
                                fullWidth
                                variant="standard"
                                value={model.physicalRestrictions ?? ''}
                                onChange={(e) => setModel({ physicalRestrictions: e.target.value })}
                                inputProps={{ maxLength: RTW_PHYSICAL_RESTRICTIONS_MAX }}
                                error={showErrors && !!fieldErrors.physicalRestrictions}
                                helperText={showErrors ? fieldErrors.physicalRestrictions ?? ' ' : ' '}
                                {...fieldHelperProps}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Primary Language"
                                required
                                fullWidth
                                variant="standard"
                                value={model.primaryLanguage ?? ''}
                                onChange={(e) => setModel({ primaryLanguage: e.target.value })}
                                inputProps={{ maxLength: RTW_PRIMARY_LANGUAGE_MAX }}
                                error={showErrors && !!fieldErrors.primaryLanguage}
                                helperText={showErrors ? fieldErrors.primaryLanguage ?? ' ' : ' '}
                                {...fieldHelperProps}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <TextField
                                label="Hourly Rate of Pay Employer will Pay while in Return to Work"
                                required
                                fullWidth
                                variant="standard"
                                value={model.hourlyRate ?? ''}
                                onChange={(e) => setModel({ hourlyRate: e.target.value })}
                                inputProps={{ maxLength: RTW_HOURLY_RATE_MAX }}
                                error={showErrors && !!fieldErrors.hourlyRate}
                                helperText={showErrors ? fieldErrors.hourlyRate ?? ' ' : ' '}
                                {...fieldHelperProps}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <FormControl
                                fullWidth
                                variant="standard"
                                error={showErrors && !!fieldErrors.isUnionMember}
                            >
                                <Typography variant="caption">Is the Injured Worker a Union Member? *</Typography>
                                <Select
                                    value={model.isUnionMember ?? ''}
                                    onChange={(e) => setModel({ isUnionMember: e.target.value as string })}
                                    displayEmpty
                                >
                                    <MenuItem value="">
                                        <em>Select</em>
                                    </MenuItem>
                                    {RTW_UNION_MEMBER_OPTIONS.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {showErrors && fieldErrors.isUnionMember && (
                                    <FormHelperText>{fieldErrors.isUnionMember}</FormHelperText>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                className={rtwClasses.rtwCheckboxLabel}
                                control={
                                    <Checkbox
                                        checked={!!model.requiresAlternateHoursEnabled}
                                        onChange={(_, checked) =>
                                            setModel({
                                                requiresAlternateHoursEnabled: checked,
                                                transitionHoursPerWeek: checked ? model.transitionHoursPerWeek : '',
                                            })
                                        }
                                        color="secondary"
                                    />
                                }
                                label={
                                    <Typography component="span" className={rtwClasses.rtwFieldLabel}>
                                        {RTW_ALTERNATE_HOURS_CHECKBOX_LABEL}
                                    </Typography>
                                }
                            />
                        </Grid>
                        {model.requiresAlternateHoursEnabled && (
                            <Grid item xs={12}>
                                <TextField
                                    label={RTW_HOURS_PER_WEEK_FIELD_LABEL}
                                    required
                                    fullWidth
                                    variant="standard"
                                    value={model.transitionHoursPerWeek ?? ''}
                                    onChange={(e) => setModel({ transitionHoursPerWeek: e.target.value })}
                                    inputProps={{ maxLength: RTW_TRANSITION_HOURS_MAX }}
                                    error={showErrors && !!fieldErrors.transitionHoursPerWeek}
                                    helperText={showErrors ? fieldErrors.transitionHoursPerWeek ?? ' ' : ' '}
                                    {...fieldHelperProps}
                                />
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <FormControlLabel
                                className={rtwClasses.rtwCheckboxLabel}
                                control={
                                    <Checkbox
                                        checked={!!model.requiresAlternateScheduleEnabled}
                                        onChange={(_, checked) =>
                                            setModel({
                                                requiresAlternateScheduleEnabled: checked,
                                                workSchedulePreferences: checked ? model.workSchedulePreferences : '',
                                            })
                                        }
                                        color="secondary"
                                    />
                                }
                                label={
                                    <Typography component="span" className={rtwClasses.rtwFieldLabel}>
                                        {RTW_ALTERNATE_SCHEDULE_CHECKBOX_LABEL}
                                    </Typography>
                                }
                            />
                        </Grid>
                        {model.requiresAlternateScheduleEnabled && (
                            <Grid item xs={12}>
                                <TextField
                                    label="Work Schedule Preferences"
                                    required
                                    fullWidth
                                    variant="standard"
                                    value={model.workSchedulePreferences ?? ''}
                                    onChange={(e) => setModel({ workSchedulePreferences: e.target.value })}
                                    inputProps={{ maxLength: RTW_WORK_SCHEDULE_MAX }}
                                    error={showErrors && !!fieldErrors.workSchedulePreferences}
                                    helperText={showErrors ? fieldErrors.workSchedulePreferences ?? ' ' : ' '}
                                    {...fieldHelperProps}
                                />
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <FormControl
                                fullWidth
                                variant="standard"
                                error={showErrors && !!fieldErrors.lightDutyLocation}
                            >
                                <Typography variant="caption">
                                    Which location would you like the Injured worker to work light duty? *
                                </Typography>
                                <Select
                                    value={model.lightDutyLocation ?? ''}
                                    onChange={(e) => setModel({ lightDutyLocation: e.target.value as string })}
                                    displayEmpty
                                >
                                    <MenuItem value="">
                                        <em>Select</em>
                                    </MenuItem>
                                    {RTW_LIGHT_DUTY_LOCATION_OPTIONS.map((option) => (
                                        <MenuItem key={option} value={option}>
                                            {option}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {showErrors && fieldErrors.lightDutyLocation && (
                                    <FormHelperText>{fieldErrors.lightDutyLocation}</FormHelperText>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Additional Comments"
                                multiline
                                minRows={3}
                                fullWidth
                                variant="standard"
                                value={model.additionalInformation ?? ''}
                                onChange={(e) => setModel({ additionalInformation: e.target.value })}
                                inputProps={{ maxLength: RTW_ADDITIONAL_COMMENTS_MAX }}
                                placeholder={getRtwAdditionalCommentsPlaceholder(model.serviceTypeId)}
                                helperText={`${(model.additionalInformation ?? '').length}/${RTW_ADDITIONAL_COMMENTS_MAX}`}
                                FormHelperTextProps={{ style: { marginTop: 4 } }}
                            />
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>
        </Grid>
    );
};
